import { useEffect, useState, useRef } from 'react';
import { useNow } from '@/hooks/useNow';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import { InlineTagText } from './InlineTagText';

const demoLines = [
  'most note taking apps treat time as #metadata you can\'t see.',
  'in notetime, notes are just text with #timestamps and #tags. that’s it.',
  'notes stay #local. no account, no signup.',
  'you can also paste #images, they attach to the timestamp',
];

/** Index of the line that carries the photo attachments. */
const IMAGE_LINE_INDEX = demoLines.length - 1;
/** Beat between the image line committing and the photo strip appearing. */
const STRIP_DELAY_MS = 450;
/** Beat between the strip landing and the idle ticking line appearing. */
const IDLE_DELAY_MS = 700;
const THUMB_STAGGER_MS = 120;

type Line = { time: Date; text: string };

/**
 * Small photo-like illustrations rendered inline so the demo ships no
 * external images. Fixed colors are fine: the landing page is light-only.
 * Two subjects so the pair reads as two real pasted photos: a whiteboard
 * sketch from work and a sunset from life.
 */
export function PhotoThumb({
  variant,
  visible,
  index,
}: {
  variant: 'whiteboard' | 'sunset';
  visible: boolean;
  index: number;
}) {
  return (
    <div
      className={`h-14 w-14 shrink-0 overflow-hidden rounded-md border border-border/60 motion-safe:transition-[opacity,transform] motion-safe:duration-200 motion-safe:ease-[cubic-bezier(0.25,1,0.5,1)] ${
        visible ? 'scale-100 opacity-100' : 'scale-[0.85] opacity-0'
      }`}
      style={{ transitionDelay: `${index * THUMB_STAGGER_MS}ms` }}
    >
      <svg viewBox="0 0 56 56" className="block h-full w-full" role="presentation">
        {variant === 'whiteboard' ? (
          <>
            {/* Office wall + whiteboard with a marker sketch */}
            <rect width="56" height="56" fill="#e7ebf0" />
            <rect x="5" y="7" width="46" height="38" rx="2" fill="#ffffff" stroke="#c9d1da" />
            <rect x="10" y="13" width="13" height="9" rx="1.5" fill="none" stroke="#4a7dbd" strokeWidth="1.8" />
            <path d="M23.5 17.5 H34" stroke="#d95550" strokeWidth="1.8" strokeLinecap="round" />
            <path d="M31.5 14.5 L35 17.5 L31.5 20.5" fill="none" stroke="#d95550" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            <rect x="36" y="12" width="10" height="10" rx="1" fill="#f4d35e" transform="rotate(4 41 17)" />
            <path d="M10 32 C 15 28, 20 36, 26 31 S 38 29, 44 33" fill="none" stroke="#5aa06a" strokeWidth="1.8" strokeLinecap="round" />
            <path d="M10 38.5 H30" stroke="#8b93a1" strokeWidth="1.6" strokeLinecap="round" />
            <rect x="20" y="45" width="16" height="3" rx="1.5" fill="#c9d1da" />
          </>
        ) : (
          <>
            {/* Sunset over water */}
            <defs>
              <linearGradient id="thumb-sunset-sky" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#fbe3b5" />
                <stop offset="0.65" stopColor="#f2a65e" />
                <stop offset="1" stopColor="#e88d5a" />
              </linearGradient>
              <linearGradient id="thumb-sunset-sea" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#e0885c" />
                <stop offset="1" stopColor="#b06046" />
              </linearGradient>
            </defs>
            <rect width="56" height="38" fill="url(#thumb-sunset-sky)" />
            <circle cx="36" cy="26" r="7" fill="#fff3d6" />
            <path d="M0 30 L12 18 L22 28 L28 23 L38 33 L56 26 V38 H0 Z" fill="#9c5a72" opacity="0.85" />
            <rect y="36" width="56" height="20" fill="url(#thumb-sunset-sea)" />
            <path d="M30 41 H42 M32 45 H40 M33 49 H39" stroke="#ffe4b8" strokeWidth="1.4" strokeLinecap="round" opacity="0.8" />
          </>
        )}
      </svg>
    </div>
  );
}

export default function AnimatedEditor() {
  const time = useNow();
  const [currentLine, setCurrentLine] = useState('');
  const [lines, setLines] = useState<Line[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showStrip, setShowStrip] = useState(false);
  const [showIdle, setShowIdle] = useState(false);
  const demoTextRef = useRef<HTMLDivElement>(null);
  const reducedMotion = usePrefersReducedMotion();

  // Reduced motion: render all lines and the photo strip at once with stamp
  // offsets, skip typing.
  useEffect(() => {
    if (!reducedMotion) return;

    const baseline = new Date();
    setLines(
      demoLines.map((text, i) => {
        const t = new Date(baseline);
        t.setSeconds(t.getSeconds() - (demoLines.length - 1 - i) * 12);
        return { time: t, text };
      }),
    );
    setCurrentStep(demoLines.length);
    setShowStrip(true);
  }, [reducedMotion]);

  useEffect(() => {
    if (reducedMotion) return;
    if (currentStep >= demoLines.length) return;

    const targetText = demoLines[currentStep];

    // Hold on the empty editor briefly on mount so hero animations and font
    // loads can settle before typing kicks off.
    const initialDelay = currentStep === 0 ? 700 : 0;

    let typeInterval: ReturnType<typeof setInterval> | undefined;
    let nextStepTimeout: ReturnType<typeof setTimeout> | undefined;

    const startTimeout = setTimeout(() => {
      setIsTyping(true);
      let i = 0;
      typeInterval = setInterval(() => {
        if (i <= targetText.length) {
          setCurrentLine(targetText.substring(0, i));
          i++;
        } else {
          if (typeInterval) clearInterval(typeInterval);
          setIsTyping(false);
          setLines((prev) => [...prev, { time: new Date(), text: targetText }]);
          setCurrentLine('');
          nextStepTimeout = setTimeout(() => setCurrentStep((prev) => prev + 1), 450);
        }
      }, 45);
    }, initialDelay);

    return () => {
      clearTimeout(startTimeout);
      if (typeInterval) clearInterval(typeInterval);
      if (nextStepTimeout) clearTimeout(nextStepTimeout);
    };
  }, [currentStep, reducedMotion]);

  // A beat after the image line commits, the photos land.
  useEffect(() => {
    if (reducedMotion || showStrip) return;
    if (lines.length <= IMAGE_LINE_INDEX) return;
    const stripTimeout = setTimeout(() => setShowStrip(true), STRIP_DELAY_MS);
    return () => clearTimeout(stripTimeout);
  }, [reducedMotion, lines.length, showStrip]);

  // Once the strip has landed, the idle ticking line takes over.
  useEffect(() => {
    if (reducedMotion || !showStrip || showIdle) return;
    const idleTimeout = setTimeout(() => setShowIdle(true), IDLE_DELAY_MS);
    return () => clearTimeout(idleTimeout);
  }, [reducedMotion, showStrip, showIdle]);

  useEffect(() => {
    if (demoTextRef.current) {
      demoTextRef.current.scrollTop = demoTextRef.current.scrollHeight;
    }
  }, [lines, currentLine]);

  const formatTime = (date: Date) =>
    date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

  return (
    <div className="relative" aria-hidden="true">
      <div
        ref={demoTextRef}
        className="min-h-[428px] px-4 py-5 font-mono text-sm leading-[1.75] sm:min-h-[305px] sm:px-6"
      >
        {lines.map((line, i) => (
          <div key={i}>
            <div className="flex items-baseline gap-3 py-0.5">
              <span className="shrink-0 text-muted-foreground/80 transition-colors hover:text-muted-foreground">
                {formatTime(line.time)}
              </span>
              <span className="flex-1 break-words text-foreground">
                <InlineTagText text={line.text} />
              </span>
            </div>
            {i === IMAGE_LINE_INDEX && (
              <div className="mt-1.5 flex items-baseline gap-3">
                {/* Invisible twin of the timestamp keeps the strip aligned
                    under the line's content column. */}
                <span aria-hidden className="invisible shrink-0">
                  {formatTime(line.time)}
                </span>
                <div className="flex h-14 flex-1 items-center gap-2">
                  <PhotoThumb variant="whiteboard" visible={showStrip} index={0} />
                  <PhotoThumb variant="sunset" visible={showStrip} index={1} />
                </div>
              </div>
            )}
          </div>
        ))}

        {!reducedMotion && currentLine !== '' && (
          <div className="flex items-baseline gap-3 py-0.5">
            <span className="shrink-0 text-muted-foreground/80 transition-colors hover:text-muted-foreground">
              {formatTime(time)}
            </span>
            <span className="flex-1 break-words text-foreground">
              <InlineTagText text={currentLine} excludeTrailingTag />
              <span
                className="ml-[1px] inline-block h-[1em] w-[2px] -translate-y-[1px] bg-foreground align-middle animate-cursor-blink"
                aria-hidden
              />
            </span>
          </div>
        )}

        {!reducedMotion && showIdle && currentLine === '' && !isTyping && currentStep >= demoLines.length && (
          <div className="mt-1 flex items-baseline gap-3 py-0.5">
            <span className="shrink-0 text-muted-foreground/80 transition-colors hover:text-muted-foreground">
              {formatTime(time)}
            </span>
            <span className="flex-1 break-words text-muted-foreground/60">
              <span
                className="inline-block h-[1em] w-[2px] -translate-y-[1px] bg-foreground align-middle animate-cursor-blink"
                aria-hidden
              />
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
